import { useEffect } from 'react';
import type {
  AnalyticsEvent,
  DemoRequestEvent,
  FormSubmissionEvent,
  ScrollDepthEvent,
  ContentEngagementEvent,
  NavigationEvent,
  FeatureDiscoveryEvent,
  PageViewEvent,
  UserEvent,
  FeatureEvent,
  LoginEvent,
  CalendarRequestEvent
} from '../types/analytics';

declare global {
  interface Window {
    dataLayer: any[];
  }
}

export const useAnalytics = () => {
  // Initialize dataLayer if it doesn't exist
  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
  }, []);

  const pushToDataLayer = (data: AnalyticsEvent) => {
    if (window.dataLayer) {
      window.dataLayer.push(data);
    }
  };

  // Page View Tracking
  const trackPageView = (data: Omit<PageViewEvent, 'event'>) => {
    pushToDataLayer({
      event: 'pageView',
      ...data,
      timestamp: new Date().toISOString()
    });
  };

  // User Tracking
  const trackUserData = (data: Omit<UserEvent, 'event'>) => {
    pushToDataLayer({
      event: 'userUpdate',
      ...data,
      timestamp: new Date().toISOString()
    });
  };

  // Feature Interaction Tracking
  const trackFeatureInteraction = (data: Omit<FeatureEvent, 'event'>) => {
    pushToDataLayer({
      event: 'featureInteraction',
      ...data,
      timestamp: new Date().toISOString()
    });
  };

  // Demo Request Tracking
  const trackDemoRequest = (data: Omit<DemoRequestEvent, 'event'>) => {
    pushToDataLayer({
      event: 'demoRequest',
      ...data,
      timestamp: new Date().toISOString()
    });
  };

  // Enhanced Form Submission Tracking
  const trackFormSubmission = (data: Omit<FormSubmissionEvent, 'event'>) => {
    pushToDataLayer({
      event: 'form_submission',
      ...data,
      timestamp: new Date().toISOString(),
      pageUrl: window.location.href,
      pagePath: window.location.pathname
    });
  };

  // Scroll Depth Tracking
  const trackScrollDepth = (data: Omit<ScrollDepthEvent, 'event'>) => {
    pushToDataLayer({
      event: 'scrollDepth',
      ...data,
      timestamp: new Date().toISOString()
    });
  };

  // Content Engagement Tracking
  const trackContentEngagement = (data: Omit<ContentEngagementEvent, 'event'>) => {
    pushToDataLayer({
      event: 'contentEngagement',
      ...data,
      timestamp: new Date().toISOString()
    });
  };

  // Navigation Tracking
  const trackNavigation = (data: Omit<NavigationEvent, 'event'>) => {
    pushToDataLayer({
      event: 'navigation',
      ...data,
      timestamp: new Date().toISOString()
    });
  };

  // Feature Discovery Tracking
  const trackFeatureDiscovery = (data: Omit<FeatureDiscoveryEvent, 'event'>) => {
    pushToDataLayer({
      event: 'featureDiscovery',
      ...data,
      timestamp: new Date().toISOString()
    });
  };

  // Login Tracking
  const trackLogin = (data: Omit<LoginEvent, 'event'>) => {
    pushToDataLayer({
      event: 'login_attempt',
      ...data,
      timestamp: new Date().toISOString(),
      pageUrl: window.location.href,
      pagePath: window.location.pathname
    });
  };

  // Calendar/Demo Request Tracking
  const trackCalendarRequest = (data: Omit<CalendarRequestEvent, 'event'>) => {
    pushToDataLayer({
      event: 'calendar_request',
      ...data,
      timestamp: new Date().toISOString(),
      pageUrl: window.location.href,
      pagePath: window.location.pathname,
      // Capture UTM parameters if available
      utmSource: new URLSearchParams(window.location.search).get('utm_source') || undefined,
      utmMedium: new URLSearchParams(window.location.search).get('utm_medium') || undefined,
      utmCampaign: new URLSearchParams(window.location.search).get('utm_campaign') || undefined
    });
  };

  // Generic Event Tracking
  const trackEvent = (eventName: string, eventData: object) => {
    pushToDataLayer({
      event: eventName,
      ...eventData,
      timestamp: new Date().toISOString()
    });
  };

  return {
    trackPageView,
    trackUserData,
    trackFeatureInteraction,
    trackDemoRequest,
    trackFormSubmission,
    trackScrollDepth,
    trackContentEngagement,
    trackNavigation,
    trackFeatureDiscovery,
    trackLogin,
    trackCalendarRequest,
    trackEvent
  };
};

export default useAnalytics; 